import { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import MuiLink from "@mui/material/Link";
import InputLabel from "@mui/material/InputLabel";
import DeleteIcon from "@mui/icons-material/Delete";
import { FormHelperText, FormControl, Button, MenuItem, Select, Grid, TextField } from "@mui/material";
import * as constants from "../../utils/constants";
import ErrorMessage from "../../Components/ErrorMessage";
import Message from "../../Components/Message";
import { fetchTranslations } from "../../utils/hooks";
import validator from "validator";
import {
  getToken,
  fetchData,
  fetchDataFromAPI,
  updateData,
  formatDate,
  removeNonLettersCharacter,
  useErrorHandler,
  isStrongPassword,
} from "../../utils/hooks";
import { TranslationContext } from "../../Components/TranslationContext";

function User() {
  const { errorMessage, handleAPIError } = useErrorHandler();
  const fileInputRef = useRef(null);
  const [signatureError, setSignatureError] = useState("");
  const [formValues, setFormValues] = useState({
    id: "",
    username: "",
    first_name: "",
    last_name: "",
    street: "",
    zipcode: "",
    city: "",
    lang: "",
    valid_until: "",
    new_password: "",
    new_password_again: "",
    signature_filename: "",
  });
  const { translations, updateTranslations, wizard, setWizard } = useContext(TranslationContext);

  const [languages, setLanguages] = useState([]);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);
  const [timeOutDuration, setTimeOutDuration] = useState(3000);
  const [previousLanguage, setPreviousLanguage] = useState("");

  const displayedLabels = {
    username: translations.email,
    first_name: translations.first_name,
    last_name: translations.last_name,
    street: translations.street,
    zipcode: translations.zipcode,
    city: translations.city,
    lang: translations.language_display,
    valid_until: translations.subscription_ending,
    new_password: translations.new_password,
    new_password_again: translations.new_password_again,
  };

  const fieldType = {
    username: "text",
    first_name: "text",
    last_name: "text",
    street: "text",
    zipcode: "text",
    city: "text",
    lang: "select",
    valid_until: "none",
    new_password: "text",
    new_password_again: "text",
    signature_filename: "none",
  };

  const [formErrors, setFormErrors] = useState({
    first_name: "",
    last_name: "",
    street: "",
    zipcode: "",
    city: "",
    lang: "",
    new_password: "",
    new_password_again: "",
  });

  const auth = `Bearer ${getToken()}`;
  const requestOptions = useMemo(
    () => ({
      headers: {
        Authorization: auth,
      },
    }),
    [auth]
  );
  const API_URL = `${constants.API_URL}user`;
  const API_URL_LANGUAGES = `${constants.API_URL}languages`;
  const API_URL_SIGNATURE = `${constants.API_URL}user/addsignature/${localStorage.getItem("userId")}`;
  const API_URL_REMOVE_SIGNATURE = `${constants.API_URL}user/removesignature/${localStorage.getItem("userId")}`;

  const fetchUserData = useCallback(() => {
    fetchData(API_URL, requestOptions)
      .then((data) => {
        data.new_password = "";
        data.new_password_again = "";
        setFormValues(data);
        setPreviousLanguage(data.lang);
      })
      .catch((error) => handleAPIError(error));
  }, [API_URL, requestOptions, handleAPIError]);

  const fetchLanguages = useCallback(() => {
    fetchDataFromAPI(API_URL_LANGUAGES, requestOptions, setLanguages, false).catch((error) => handleAPIError(error));
  }, [API_URL_LANGUAGES, requestOptions, handleAPIError]);

  const handleTextFieldChange = (key) => (event) => {
    let updatedValue = event.target.value;

    // For zipcode
    if (key === "zipcode") {
      // replace all non-numeric characters by nothing
      updatedValue = updatedValue.replace(/\D/g, "");
    } else if (key === "first_name" || key === "last_name" || key === "city") {
      updatedValue = removeNonLettersCharacter(updatedValue, false);
    } else if (key === "street") {
      updatedValue = removeNonLettersCharacter(updatedValue, true);
    }

    setFormValues((prevValues) => ({
      ...prevValues,
      [key]: updatedValue,
    }));
  };

  const uploadSignature = (file) => {
    const formData = new FormData();
    formData.append("signature", file);

    fetch(API_URL_SIGNATURE, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
      body: formData,
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((err) => {
            throw new Error(err.message);
          });
        }
        return response.json();
      })
      .then((data) => {
        setMessage(data.message);
        setMessageType("info");
        setTimeOutDuration(timeOutDuration + 1);
        setSignatureError("");
        fetchUserData();
      })
      .catch((error) => {
        setSignatureError(error.message);
      });
  };

  const removeSignature = () => {
    fetch(API_URL_REMOVE_SIGNATURE, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((err) => {
            throw new Error(err.message);
          });
        }
        return response.json();
      })
      .then((data) => {
        setMessage(data.message);
        setMessageType("info");
        setFormValues((prev) => ({ ...prev, signature_filename: "" }));
        fetchUserData();
      })
      .catch((error) => {
        handleAPIError(error);
      });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    // Validate file type
    if (file.type !== "image/jpeg" && file.type !== "image/png") {
      setSignatureError(translations.signature_invalid_type);
      return;
    }

    // Validate file dimensions
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        if (img.width > 300 || img.height > 200) {
          setSignatureError(
            translations.signature_invalid_dimensions
              .replace("%s1", "300x200")
              .replace("%s2", `${img.width}x${img.height}`)
          );
        } else {
          setSignatureError("");
          uploadSignature(file);
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      formValues.id = localStorage.getItem("userId");
      updateData(API_URL, formValues.id, formValues, getToken())
        .then((response) => {
          if (wizard < 2) {
            setWizard(2);
          }
          if (previousLanguage !== formValues.lang) {
            async function loadTranslations() {
              try {
                const translationsData = await fetchTranslations(formValues.lang);
                localStorage.setItem("translations", JSON.stringify(translationsData));
                updateTranslations(translationsData);
                localStorage.setItem("userLanguage", formValues.lang);
              } catch (error) {
                handleAPIError(error);
              }
            }

            loadTranslations();
          }
          setMessage(translations.update_done);
          setMessageType("info");
          setTimeOutDuration(timeOutDuration + 1);
        })
        .catch((error) => {
          handleAPIError(error);
        });
    }
  };

  const validateForm = () => {
    let isValid = true;
    const errors = {
      name: "",
      street: "",
      zipcode: "",
      city: "",
      email: "",
      lang: "",
      new_password: "",
      new_password_again: "",
    };

    /**
     * All fields are mandatory excepted password update
     * Nevertheless, if first password field is set, second
     * field must be set also
     */
    for (const [key, value] of Object.entries(formValues)) {
      if (!key.startsWith("new_password") && key !== "signature_filename") {
        if (value === null || validator.isEmpty(String(value))) {
          isValid = false;
          errors[key] = translations.required_field.replace("%s", displayedLabels[key]);
        }
      } else if (key === "new_password" && value.length > 0) {
        if (value !== formValues["new_password_again"]) {
          errors["new_password_again"] = translations.new_password_mismatch;
          isValid = false;
        } else if (!isStrongPassword(value)) {
          errors["new_password_again"] = translations.password_not_match_complexity;
          errors["new_password"] = translations.password_not_match_complexity;
          isValid = false;
        }
      }
      setFormErrors(errors);
    }
    return isValid;
  };

  useEffect(() => {
    fetchUserData();
    fetchLanguages();
  }, [fetchUserData, fetchLanguages]);
  /*
  const leftColumnKeys = Object.keys(formValues).filter(
    (key) => key !== "id" && !key.startsWith("new_password") && fieldType[key] === "text"
  );
*/
  const leftColumnKeys = ["username", "first_name", "last_name", "street", "zipcode", "city"];
  /*
  const rightColumnKeys = Object.keys(formValues).filter(
    (key) => key !== "id" && (fieldType[key] !== "text" || key.startsWith("new_password"))
  );
  */
  const rightColumnKeys = ["lang", "new_password", "new_password_again", "signature_filename"];
  return (
    <div>
      <h1>{translations.profile_update_title}</h1>
      <Message message={message} timeout={timeOutDuration} type={messageType} />
      <ErrorMessage message={errorMessage} />
      <div className="mainPageContainer">
        <form onSubmit={handleSubmit}>
          <div className="userSubscriptionValidity">
            <div>
              {translations.subscription_ending}
              {formatDate({ value: formValues.valid_until })}
            </div>
            {true && (
              <div className="listButton">
                <MuiLink component={Link} to="/Renew">
                  <AutorenewIcon></AutorenewIcon>&nbsp;
                  {translations.subscription_renew_action}
                </MuiLink>
              </div>
            )}
          </div>
          <br />
          <Grid container spacing={4}>
            <Grid item xs={6}>
              <Grid container spacing={2} direction="column">
                {leftColumnKeys.map((key) => (
                  <Grid item key={key}>
                    <TextField
                      label={displayedLabels[key]}
                      value={formValues[key]}
                      onChange={handleTextFieldChange(key)}
                      error={!!formErrors[key]}
                      helperText={formErrors[key]}
                      color="primary"
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Grid container spacing={1} direction="column">
                {rightColumnKeys.map((key) => {
                  const value = formValues[key];
                  return (
                    <Grid item key={key}>
                      {fieldType[key] === "select" && key === "lang" && (
                        <div>
                          <FormControl sx={{ m: 1, minWidth: 120 }}>
                            <InputLabel id="{key}-label">{translations.language_display}</InputLabel>

                            <Select
                              id={key}
                              value={value}
                              label={translations.language_display}
                              onChange={handleTextFieldChange(key)}
                              error={!!formErrors[key]}
                            >
                              <MenuItem value="">{translations.select_an_option}</MenuItem>
                              {languages.map((languageCode) => (
                                <MenuItem key={key + "_" + languageCode} value={languageCode}>
                                  {translations[`language_${languageCode.substring(0, 2)}`]}
                                </MenuItem>
                              ))}
                            </Select>
                            {formErrors[key] && <FormHelperText error>{formErrors[key]}</FormHelperText>}
                          </FormControl>
                        </div>
                      )}
                      {fieldType[key] === "text" && key.startsWith("new_password") && (
                        <div>
                          {key === "new_password" && (
                            <FormControl component="fieldset">
                              <h3 id="password_update_text">
                                {translations.modify_password}
                                &nbsp;
                                <Tooltip title={translations.password_rules_tooltip} arrow>
                                  <InfoIcon></InfoIcon>
                                </Tooltip>
                              </h3>
                            </FormControl>
                          )}
                          <TextField
                            label={displayedLabels[key]}
                            value={value}
                            onChange={handleTextFieldChange(key)}
                            error={!!formErrors[key]}
                            helperText={formErrors[key]}
                            color="primary"
                          />
                        </div>
                      )}
                    </Grid>
                  );
                })}
                <Grid item key="receiptSignature">
                  <div className="signature-section">
                    <input
                      type="file"
                      accept="image/png, image/jpeg"
                      style={{ display: "none" }}
                      ref={fileInputRef}
                      onChange={handleFileChange}
                    />
                    {formValues.signature_filename ? (
                      <Button variant="contained" color="error" onClick={removeSignature} startIcon={<DeleteIcon />}>
                        {translations.delete_signature}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={() => fileInputRef.current.click()}
                        startIcon={<DriveFileRenameOutlineIcon />}
                      >
                        {translations.sign_receipts}
                      </Button>
                    )}
                    <ErrorMessage message={signatureError} />
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <div className="flex flexHorizontalCenter" style={{ marginTop: "20px" }}>
            <Button type="submit" variant="contained" color="primary">
              {translations.save}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default User;
